import React, { useState, useContext, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { TranscriptViewModel } from "./TranscriptViewModel";
import TranscriptMessage from "./TranscriptMessage";
import { APIContext } from "src/components/Contexts/APIContext"
import TranscriptTextFinder from "./TranscriptTextFinder";
import TranscriptTextFinderBar from "./TranscriptTextFinderBar";
import TranscriptSearch from "./TranscriptSearch";
import Stack from "@mui/material/Stack";



interface IProps {
    id: string
    switchCallId?: string
    transcriptSearch?: TranscriptSearch
    mainView?: boolean;
}

export default function TranscriptTab(props: IProps) {
    const { get } = useContext(APIContext);
    const [agentName, setAgentName] = useState<string>("");
    const [customerName, setCustomerName] = useState<string>("");
    const [transcript, setTranscript] = useState<TranscriptViewModel | null>(null);
    const [loadingTranscript, setTranscriptLoading] = useState<boolean>(false);
    const [transcriptTextFinder, setTranscriptTextFinder] = useState<TranscriptTextFinder | undefined>(undefined);

    useEffect(() => {
        getAgentName();
        getCustomerName();
    }, [props.id])

    // Web chat transcript use id rather than switchCallId
    useEffect(() => {
        if (props.switchCallId) {
            getTranscript(props.switchCallId);
        }
        else if (props.id) {
            getTranscript(props.id);
        }
    }, [props.switchCallId,])

    const getAgentName = () => {
        get('MonitoringCallInfo', `GetAgentName?systemPK=${props.id}`, (data) => setAgentName(data));
    }

    const getCustomerName = () => {
        get('MonitoringCallInfo', `GetCustomerName?systemPK=${props.id}`, (data) => setCustomerName(data));
    }

    const getTranscript = (id: string) => {
        setTranscriptLoading(true);
        get('Transcript', `GetTranscript?switchCallId=${id}`, (data) => {
            setTranscript(data);
            setTranscriptLoading(false);
        })
    }

    const messages = useMemo(() => transcript?.messages.map((message, index) => <TranscriptMessage
        message={message}
        agentName={agentName}
        customerName={customerName}
        key={index}
        transcriptSearch={props.transcriptSearch}
        textFinderViewModel={transcriptTextFinder}
        messageIndex={index}
        lastMessage={index + 1 === transcript!.messages.length}
    />), [transcript?.messages, transcriptTextFinder?.searchText])

    return (
        loadingTranscript ?
            <Grid container spacing={1} justifyContent="center" alignItems="center" padding="40%" sx={{ height: "100%" }}>
                <Grid>
                    <CircularProgress size={50} />
                </Grid>
            </Grid>
            :
            <Stack sx={{
                overflow: 'hidden',
                height: '100%'
            }}>
                {
                    transcript &&
                    <Grid container size={12} sx={{
                        backgroundColor: '#f5f5f5',
                        justifyContent: {
                            xs: 'center',
                            xl: 'space-between'
                        },
                    }}>
                        <Grid
                            style={{
                                alignContent: 'space-evenly',
                                paddingLeft: '8px'
                            }}
                            sx={{
                                size: {
                                    xs: 12,
                                    xl: props.mainView ? 6 : 12,
                                },
                                justifyContent: {
                                    xs: 'center',
                                    xl: 'flex-start'
                                },
                            }}>
                        </Grid>
                        <Grid
                            sx={{
                                size: {
                                    xs: 12,
                                    xl: props.mainView ? 6 : 12
                                },
                                justifyContent: {
                                    xs: 'center',
                                }
                            }}>
                            <TranscriptTextFinderBar
                                transcript={transcript}
                                transcriptSearch={props.transcriptSearch}
                                transcriptTextFinder={transcriptTextFinder}
                                setTranscriptTextFinder={setTranscriptTextFinder}
                            />
                        </Grid>
                    </Grid>
                }
                <Box sx={{
                    overflow: 'auto',
                    backgroundColor: '#fff',
                    padding: '8px',
                }}>
                    <Grid container spacing={1} justifyContent="center" alignItems="center" marginBottom={4} sx={{ height: "100%" }}>
                        {
                            (transcript) ?
                                messages
                                :
                                <Grid size={12}>
                                    <Typography variant="h4" textAlign="center">No transcript found for this call</Typography>
                                </Grid>
                        }
                    </Grid>
                </Box >
            </Stack >
    );
}